import type { IndustrialCaseDocument } from '@/domain/industrialCase/industrialCaseDocument';
import Link from '@/routing/AppLink';

import {
  buildInquiryHref,
  industrialCaseCtaSource,
} from '@/components/apps/ui/inquiryForm/inquiryCtaSource';

type Props = {
  /** Slug of the case, used to tag the inquiry link. */
  caseId: string;
  /** The headline number shown large at the top. */
  metric: IndustrialCaseDocument['primaryMetric'];
  /** Remaining result metrics; the headline one is dropped if repeated here. */
  outcomes: ReadonlyArray<IndustrialCaseDocument['outcomes'][number]>;
  inquiryLabel: string;
  metricLabel: string;
  isInteractive?: boolean;
};

/**
 * ### IndustrialCaseMetricSidebar
 *
 * Displays the primary result and supporting metrics beside the article.
 * Preview mode preserves the inquiry affordance without allowing navigation.
 *
 * @param props Metrics, labels, and interaction mode
 * @returns The result summary sidebar
 */
export function IndustrialCaseMetricSidebar({
  caseId,
  metric,
  outcomes,
  inquiryLabel,
  metricLabel,
  isInteractive = true,
}: Props) {
  const supportingMetrics = outcomes.filter(
    (outcome) =>
      outcome.value !== metric.value || outcome.label !== metric.label,
  );

  return (
    <aside className="border-b border-[#e8e8e5] px-5 py-16 md:col-start-1 md:border-r md:border-b-0 md:px-4 md:py-26 lg:px-4">
      <div className="md:sticky md:top-30">
        <p className="text-[13px] font-semibold text-[#777773]">
          {metricLabel}
        </p>
        <div className="pb-7 pt-3 space-y-0.5">
          <strong className="block text-4xl font-semibold  text-apps-brandGrayBlack [font-variant-numeric:tabular-nums]">
            {metric.value}
          </strong>
          <span className="block text-sm font-medium text-[#6d6d69]">
            {metric.label}
          </span>
        </div>
        <dl>
          {supportingMetrics.map((outcome) => (
            <div className="py-2" key={`${outcome.value}-${outcome.label}`}>
              <dd className="text-2xl font-semibold leading-[1.15] text-apps-brandGrayBlack [font-variant-numeric:tabular-nums]">
                {outcome.value}
              </dd>
              <dt className="mt-2 text-sm font-medium leading-[1.55] text-[#6d6d69]">
                {outcome.label}
              </dt>
            </div>
          ))}
        </dl>
        {isInteractive ? (
          <Link
            className="mt-8 inline-flex min-h-11 w-full items-center justify-center bg-apps-brandGrayBlack px-4 py-3 text-sm font-semibold leading-none text-apps-coreGrayWhite transition-colors hover:bg-apps-corePrimary01 active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01"
            href={buildInquiryHref({
              interest: 'poc-terms',
              source: industrialCaseCtaSource(caseId),
            })}
            prefetch={false}
          >
            {inquiryLabel}
          </Link>
        ) : (
          <span className="mt-8 inline-flex min-h-11 w-full items-center justify-center bg-apps-brandGrayBlack px-4 py-3 text-sm font-semibold leading-none text-apps-coreGrayWhite">
            {inquiryLabel}
          </span>
        )}
      </div>
    </aside>
  );
}
